import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, FastField } from 'formik';
import PropTypes from 'prop-types';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';

import LoadMask from 'components/LoadMask';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicButton from 'components/BasicButton';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicTextArea from 'components/BasicTextArea';
import BasicLabel from 'components/BasicLabel';
import LazyLoadSelect from 'components/LazyLoadSelect';
import BasicInput from 'components/BasicInput';
import RatingStarsWidgetEditable from 'components/RatingStarsWidgetEditable';
import RatingStarsWidget from 'components/RatingStarsWidget';
import FormContentLoader from 'components/FormContentLoader';

import { AddReviewCompanyFormValidationSchema } from 'scenes/Form/components/AddReviewCompanyForm/validate';
import { Role } from 'helpers/user';
import { ENDPOINT } from 'constants/endpoints';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { get, post } from 'services/api';

import './styles.css';

class AddReviewCompanyForm extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    const isCandidate = Role.isCandidate(props.params.user);
    this._initialValues = {
      isCandidate,
      firm: null,
      rating: null,
      q1: '',
      q2: '',
      q3: '',
      email: isCandidate ? null : '',
      fullname: isCandidate ? null : '',
      phone: isCandidate ? null : '',
    };

    if (props.params.firm && typeof props.params.firm === 'object') {
      this._initialValues.firm = props.params.firm;
      this._initialValues.rating = props.params.firm.rating;
    }

    this.state = {
      init: false,
      selectboxCacheUniq: `_sel_${uniqueId()}`,
    };
  }

  componentWillMount() {
    this._isMounted = true;
    const params =
      this.props.params.firm && typeof this.props.params.firm === 'object'
        ? {}
        : { firm: this.props.params.firm };

    get(ENDPOINT.FORM.GET.REVIEW_OVERVIEW, params, {
      handleSuccess: formData => {
        if (this._isMounted) {
          const data = {
            init: true,
            questions: {
              ...formData.questions,
            },
          };

          data.fields = formData.fields
            ? { ...this._initialValues, ...formData.fields }
            : { ...this._initialValues };

          this.setState(data);
        }
      },
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.params.user.roles, this.props.params.user.roles)) {
      const isCandidate = Role.isCandidate(this.props.params.user);
      this.setState({
        fields: {
          ...this.state.fields,
          isCandidate,
        },
      });
    }
  }

  handleSubmit = (values, { setSubmitting, setErrors, resetForm }) => {
    const preparedValues = clone(values);

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null) {
        delete preparedValues[field];
      } else if (field === 'isCandidate') {
        delete preparedValues[field];
      } else if (isArray(data)) {
        preparedValues[field] = data.map(item => item.id);
      } else if (typeof data === 'object') {
        preparedValues[field] = data.value;
      }
    });

    post(ENDPOINT.FORM.SAVE.REVIEW_OVERVIEW, preparedValues, {
      handleForbidden: this.handleError,
      handleError: response =>
        this._isMounted
          ? this.props.actions.handleError(response, setErrors)
          : null,
      handleSuccess: () => {
        this.setState({ selectboxCacheUniq: `_sel_${uniqueId()}` });
        resetForm();
        this.props.actions.proceed();
      },
      handleComplete: () => (this._isMounted ? setSubmitting(false) : null),
    });
  };

  getFirmOptions = (query, offset) => {
    return get(ENDPOINT.FORM.OPTIONS.REVIEW.FIRMS, {
      query,
      offset,
      type: 'overview',
    }).then(({ data }) => data);
  };

  clearForm = (callback = null) => {
    if (this._isMounted) {
      if (typeof callback === 'function') {
        this.setState(() => ({ fields: { ...this._initialValues } }), callback);
      } else {
        this.setState({ fields: { ...this._initialValues } });
      }
    }
  };

  handleChangeFirm = (field, value, { setFieldValue }) => {
    setFieldValue(field, value);
    setFieldValue('rating', value.rating);
  };

  scrollToError = isValidating => {
    if (
      !isValidating &&
      document.getElementsByClassName('form__error').length > 0
    ) {
      const elem = document.getElementsByClassName('form__error')[0];
      if (elem.previousElementSibling) {
        elem.previousElementSibling.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
  };

  render() {
    if (!this.state.init) {
      return <FormContentLoader />;
    }

    const { fields, questions } = this.state;

    return (
      <CSSTransition
        in={true}
        appear={true}
        timeout={ANIMATION_TIMEOUT_APPEAR}
        classNames="fade"
      >
        <div className="form__inner">
          <Formik
            initialValues={fields}
            validationSchema={AddReviewCompanyFormValidationSchema}
            onSubmit={this.handleSubmit}
            onReset={this.clearForm}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleReset,
              isSubmitting,
              isValidating,
              setFieldValue,
              setFieldTouched,
              submitForm,
            }) => (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  submitForm().then(() => this.scrollToError(isValidating));
                }}
                className="form__content"
              >
                {isSubmitting && <LoadMask />}
                <FastField name="firm">
                  {({ field: { name }, form: { values, errors, touched } }) => (
                    <div className="form__group form__group--compact">
                      <LazyLoadSelect
                        name={name}
                        value={values[name]}
                        isValid={
                          (!errors[name] && !!touched[name]) || !touched[name]
                        }
                        label="Firm"
                        required={true}
                        cacheUniq={this.state.selectboxCacheUniq}
                        placeholder="Start Typing"
                        getOption={this.getFirmOptions}
                        onChange={(field, value) =>
                          this.handleChangeFirm(field, value, { setFieldValue })
                        }
                        onBlur={setFieldTouched}
                      />
                      {errors[name] && !!touched[name] && (
                        <div className="form__error">{errors[name]}</div>
                      )}
                    </div>
                  )}
                </FastField>

                <FastField name="q1">
                  {({ field: { name }, form: { values, errors, touched } }) => (
                    <div className="form__group form__group--compact">
                      <BasicInput
                        label={questions[name]}
                        required={true}
                        name={name}
                        content={values[name]}
                        placeholder="Type here..."
                        controlFunc={handleChange}
                        isValid={
                          (!errors[name] && !!touched[name]) || !touched[name]
                        }
                        touchFunc={handleBlur}
                      />
                      {errors[name] && !!touched[name] && (
                        <div className="form__error">{errors[name]}</div>
                      )}
                    </div>
                  )}
                </FastField>

                <FastField name="q2">
                  {({ field: { name }, form: { values, errors, touched } }) => (
                    <div className="form__group form__group--compact">
                      <BasicLabel
                        name={name}
                        required={true}
                        text={questions[name]}
                      />
                      <div className="form__sublabel-text">
                        Some examples include: <br />
                        1) We hired or were hired to perform a service and/or
                        have a business relationship with this firm
                        <br />
                        2) This firm has an ownership stake in our business
                        and/or we raised capital from this firm
                      </div>
                      <BasicTextArea
                        rows={10}
                        name={name}
                        content={values[name]}
                        resize={false}
                        placeholder="0 characters of the 15000 character limit"
                        controlFunc={handleChange}
                        touchFunc={handleBlur}
                        isValid={
                          (!errors[name] && !!touched[name]) || !touched[name]
                        }
                      />
                      {errors[name] && !!touched[name] && (
                        <div className="form__error">{errors[name]}</div>
                      )}
                    </div>
                  )}
                </FastField>

                <FastField name="rating">
                  {({ field: { name }, form: { values } }) => (
                    <div className="form__group form__group--compact">
                      <BasicLabel
                        name={name}
                        required={true}
                        text="How would you describe your perception of this firm?"
                      />

                      {values.firm &&
                      values.firm.rating &&
                      values.firm.rating !== '0' ? (
                        <RatingStarsWidget value={values[name]} size={20} />
                      ) : (
                        <RatingStarsWidgetEditable
                          value={values[name] || 0}
                          size={20}
                          onChangeHandler={value => setFieldValue(name, value)}
                        />
                      )}
                      {errors[name] && (
                        <div className="form__error">{errors[name]}</div>
                      )}
                    </div>
                  )}
                </FastField>

                <FastField name="q3">
                  {({ field: { name }, form: { values, errors } }) => (
                    <div className="form__group form__group--compact">
                      <BasicLabel
                        name={name}
                        required={true}
                        text={questions[name]}
                      />
                      <BasicTextArea
                        rows={10}
                        name={name}
                        content={values[name]}
                        resize={false}
                        placeholder="0 characters of the 15000 character limit"
                        controlFunc={handleChange}
                        touchFunc={handleBlur}
                        isValid={
                          (!errors[name] && !!touched[name]) || !touched[name]
                        }
                      />
                      {errors[name] && !!touched[name] && (
                        <div className="form__error">{errors[name]}</div>
                      )}
                    </div>
                  )}
                </FastField>

                {!values.isCandidate && (
                  <>
                    <FastField name="email">
                      {({ field: { name }, form: { values, errors } }) => (
                        <>
                          <div className="form__group form__group--compact">
                            <BasicLabel
                              name="label"
                              required={true}
                              text="Contact Information"
                            />
                            <div className="form__sublabel-text">
                              We will keep this confidential and it will not be
                              published but it will allow us to reach out should
                              we have any follow-up questions about the
                              information provided.
                            </div>
                          </div>

                          <div className="form__group form__group--compact">
                            <BasicInput
                              label="Email"
                              required
                              name={name}
                              inputType="email"
                              content={values[name]}
                              placeholder="Type here..."
                              controlFunc={handleChange}
                              isValid={
                                (!errors[name] && !!touched[name]) ||
                                !touched[name]
                              }
                              touchFunc={handleBlur}
                            />
                            {errors[name] && !!touched[name] && (
                              <div className="form__error">{errors[name]}</div>
                            )}
                          </div>
                        </>
                      )}
                    </FastField>

                    <FastField name="fullname">
                      {({ field: { name }, form: { values, errors } }) => (
                        <div className="form__group form__group--compact">
                          <BasicInput
                            label="Full name"
                            required={true}
                            name={name}
                            content={values[name]}
                            placeholder="Type here..."
                            controlFunc={handleChange}
                            isValid={
                              (!errors[name] && !!touched[name]) ||
                              !touched[name]
                            }
                            touchFunc={handleBlur}
                          />
                          {errors[name] && !!touched[name] && (
                            <div className="form__error">{errors[name]}</div>
                          )}
                        </div>
                      )}
                    </FastField>

                    <FastField name="phone">
                      {({ field: { name }, form: { values, errors } }) => (
                        <div className="form__group form__group--compact">
                          <BasicInput
                            label="Phone number"
                            required={true}
                            name={name}
                            content={values[name]}
                            placeholder="Type here..."
                            controlFunc={handleChange}
                            isValid={
                              (!errors[name] && !!touched[name]) ||
                              !touched[name]
                            }
                            touchFunc={handleBlur}
                          />
                          {errors[name] && !!touched[name] && (
                            <div className="form__error">{errors[name]}</div>
                          )}
                        </div>
                      )}
                    </FastField>
                  </>
                )}

                <div className="form__controls form__controls--compact">
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Submit"
                    />

                    <BasicButton
                      controlFunc={handleReset}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Clear Form"
                    />
                  </BasicButtonGroup>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </CSSTransition>
    );
  }
}

AddReviewCompanyForm.propTypes = {
  params: PropTypes.shape({
    user: PropTypes.object.isRequired,
  }).isRequired,

  actions: PropTypes.shape({
    handleError: PropTypes.func.isRequired,
  }).isRequired,
};

export default AddReviewCompanyForm;
